/**
 * One place that decides WHOSE activity days a calendar's dots show.
 *
 * Every calendar with data dots (the top-bar date picker and the AI summary
 * Time Filter) describes what it is looking at as a `CalendarScope`, and
 * `buildCalendarContext` turns that into the `CalendarContext` the dots hook
 * sends. The top bar derives its scope from the URL (`calendarContextFromPath`),
 * the summary widgets from their own summary scope — but both go through the
 * same builder, so the same page shows the same dots in both calendars.
 */

export type CalendarSource =
  | 'ad-analysis'
  | 'ai-listening'
  | 'all'
  | 'content'
  | 'inbox'
  | 'review-analysis'
  | 'social-listening'

/**
 * The request context for the calendar dots:
 *
 *   content          → analysisId (optional; omitted = all analyses)
 *   review-analysis  → analysisId = review campaign (optional; omitted = all
 *                      review campaigns), facilityId / platformId (optional)
 *   social-listening → campaignId (optional; omitted = all campaigns),
 *                      keyword (optional)
 *   ai-listening     → analysisId (AI listening campaign)
 *   inbox / ad-analysis → pageId
 *   all              → every module of the user
 */
export interface CalendarContext {
  analysisId?: string
  campaignId?: string
  facilityId?: string
  keyword?: string
  pageId?: string
  platformId?: string
  source?: CalendarSource
}

export type CalendarScope =
  | { kind: 'adAnalysis'; pageId?: string }
  | { campaignId?: string; kind: 'aiListening' }
  | { kind: 'all' }
  | { analysisId?: string; kind: 'content' }
  | { kind: 'inbox'; pageId?: string }
  | {
      campaignId?: string
      facilityId?: string
      kind: 'reviewAnalysis'
      platformId?: string
    }
  | { campaignId?: string; keyword?: string; kind: 'socialListening' }

// Drops empty ids so an absent id never ends up as `?x=` or in a query key.
const compact = (context: CalendarContext): CalendarContext =>
  Object.fromEntries(
    Object.entries(context).filter(([, value]) => !!value)
  ) as CalendarContext

export const buildCalendarContext = (scope: CalendarScope): CalendarContext => {
  switch (scope.kind) {
    case 'adAnalysis':
      return compact({ pageId: scope.pageId, source: 'ad-analysis' })
    case 'aiListening':
      return compact({ analysisId: scope.campaignId, source: 'ai-listening' })
    case 'all':
      return { source: 'all' }
    case 'content':
      return compact({ analysisId: scope.analysisId, source: 'content' })
    case 'inbox':
      return compact({ pageId: scope.pageId, source: 'inbox' })
    case 'reviewAnalysis':
      return compact({
        analysisId: scope.campaignId,
        facilityId: scope.facilityId,
        platformId: scope.platformId,
        source: 'review-analysis',
      })
    case 'socialListening':
      return compact({
        campaignId: scope.campaignId,
        keyword: scope.keyword,
        source: 'social-listening',
      })
  }
}

// URL segments arrive percent-encoded (a keyword like "red wine" is
// "red%20wine" in the pathname); a malformed escape falls back to the raw text.
const decode = (segment?: string): string | undefined => {
  if (!segment) return undefined
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

// Order matters: the first match wins. Anything unmatched falls back to
// Social Listening across all campaigns (the server's own default).
const ROUTE_SCOPES: {
  pattern: RegExp
  scope: (match: RegExpMatchArray) => CalendarScope
}[] = [
  // Home and project dashboards are multi-module overviews: every module.
  { pattern: /^\/home(?:\/|$)/, scope: () => ({ kind: 'all' }) },
  { pattern: /^\/projects\/[^/]+\/?$/, scope: () => ({ kind: 'all' }) },
  {
    pattern: /^\/inbox-analysis(?:\/([^/]+))?\/?$/,
    scope: (m) => ({ kind: 'inbox', pageId: decode(m[1]) }),
  },
  {
    pattern: /^\/content-analysis(?:\/([^/]+))?\/?$/,
    scope: (m) => ({ analysisId: decode(m[1]), kind: 'content' }),
  },
  // /review-analysis                                   → all review campaigns
  // /review-analysis/:campaignId[/brand-level-widgets] → that campaign
  // /review-analysis/:campaignId/facility/:facilityId  → that facility
  // /review-analysis/:campaignId/platform/:platformId  → that platform
  {
    pattern:
      /^\/review-analysis(?:\/([^/]+)(?:\/(facility|platform)\/([^/]+))?)?(?:\/|$)/,
    scope: (m) => ({
      campaignId: decode(m[1]),
      facilityId: m[2] === 'facility' ? decode(m[3]) : undefined,
      kind: 'reviewAnalysis',
      platformId: m[2] === 'platform' ? decode(m[3]) : undefined,
    }),
  },
  // Social Listening: /campaigns/:themeId and /keywords/:themeId/:keywordName
  // (the listing pages span all campaigns).
  {
    pattern: /^\/campaigns(?:\/([^/]+))?\/?$/,
    scope: (m) => ({ campaignId: decode(m[1]), kind: 'socialListening' }),
  },
  {
    pattern: /^\/keywords(?:\/([^/]+)(?:\/([^/]+))?)?\/?$/,
    scope: (m) => ({
      campaignId: decode(m[1]),
      keyword: decode(m[2]),
      kind: 'socialListening',
    }),
  },
  // A prompt deep-dive (or its pre-redesign address) is one AI Listening
  // campaign; brand and prompt-list pages span them all.
  {
    pattern: /^\/ai-listening(?:\/(?:prompts|details)\/([^/]+))?/,
    scope: (m) => ({ campaignId: decode(m[1]), kind: 'aiListening' }),
  },
  {
    pattern: /^\/ad-analysis(?:\/ad-library\/([^/]+))?/,
    scope: (m) => ({ kind: 'adAnalysis', pageId: decode(m[1]) }),
  },
]

export const calendarScopeFromPath = (pathname: string): CalendarScope => {
  for (const { pattern, scope } of ROUTE_SCOPES) {
    const match = pathname.match(pattern)
    if (match) return scope(match)
  }
  return { kind: 'socialListening' }
}

/** The top-bar calendar's context for the page at `pathname`. */
export const calendarContextFromPath = (pathname: string): CalendarContext =>
  buildCalendarContext(calendarScopeFromPath(pathname))
